import React, { useState, useEffect } from 'react'
import { Table, Button, Modal, Input, Space, Select, Switch } from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import axios from 'axios'

import {
  UpdateClassMaster,
  CreateClassMaster,
  GetClassMaster,
  UpdateClassStatus,
} from '../../../AllAPI/AdminAPI'
import { toast } from 'react-toastify'

const ClassMaster = () => {
  const storedBranchId = sessionStorage.getItem('selected_branch')

  const [data, setData] = useState([])
  const [searchText, setSearchText] = useState('')
  const [loading, setLoading] = useState(false)

  // --- FORM STATES USING useState ---
  const [className, setClassName] = useState('')

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [modalStatus, setModalStatus] = useState(true)
  const [updatingStatus, setUpdatingStatus] = useState(false)

  useEffect(() => {
    fetchClass()
  }, [])

  const fetchClass = async () => {
    setLoading(true)
    try {
      const res = await axios.get(`${GetClassMaster}/${storedBranchId}`)

      const formatted = res.data.map((item, index) => ({
        key: item.class_id,
        index: index + 1,
        status: item.status === 1 ? 'Active' : 'Inactive',
        class_name: item.class_name,
        branch_id: item.branch_id,
      }))

      setData(formatted)
    } catch (err) {
      toast.error('Failed to load class')
    } finally {
      setLoading(false)
    }
  }

  // --- OPEN MODAL ---
  const handleOpenModal = (record = null) => {
    if (record) {
      setEditingId(record.key)
      setClassName(record.class_name)
      setModalStatus(record.status === 'Active')
    } else {
      setEditingId(null)
      setModalStatus(true)
      setClassName('')
    }
    setIsModalOpen(true)
  }

  // --- SAVE OR UPDATE ---
  const handleSave = async () => {
    if (!className.trim()) return toast.error('Please enter class name')

    const payload = {
      branch_id: storedBranchId,
      class_name: className,
    }

    try {
      if (editingId) {
        const res = await axios.put(
          `${UpdateClassMaster}/${editingId}`,
          payload,
        )
        if (res.data === 'UPDATED') {
          toast.success('Class updated successfully')
        }
      } else {
        const res = await axios.post(CreateClassMaster, payload)
        if (res.data === 'SAVED') {
          toast.success('Class added successfully')
        }
      }

      fetchClass()
      setIsModalOpen(false)
      setEditingId(null)
      setClassName('')
    } catch (err) {
      toast.error('Failed to save class')
    }
  }

  const handleModalStatusChange = async (checked) => {
    setModalStatus(checked)
    setUpdatingStatus(true)

    try {
      const payload = {
        class_id: editingId,
        status: checked ? 1 : 2,
      }

      await axios.put(UpdateClassStatus, payload)
      toast.success('Status updated')

      // update UI table instantly
      setData((prev) =>
        prev.map((item) =>
          item.key === editingId
            ? { ...item, status: checked ? 'Active' : 'Inactive' }
            : item,
        ),
      )
    } catch (err) {
      toast.error('Failed to update status')
    } finally {
      setUpdatingStatus(false)
    }
  }

  // --- SEARCH ---
  const filteredData = data.filter((item) =>
    ((item.class_name ?? '') + '')
      .toLowerCase()
      .includes(searchText.toLowerCase()),
  )

  const columns = [
    { title: 'Sr No', dataIndex: 'index' },
    { title: 'Class', dataIndex: 'class_name' },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (_, record) => (
        <span
          style={{
            padding: '4px 10px',
            borderRadius: '15px',
            fontWeight: '600',
            color: record.status === 'Active' ? 'green' : 'red',
            background: record.status === 'Active' ? '#e8ffe8' : '#ffe8e8',
          }}
        >
          {record.status === 'Active' ? 'ON' : 'OFF'}
        </span>
      ),
    },
    {
      title: 'Actions',
      align: 'center',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleOpenModal(record)}
          />
        </Space>
      ),
    },
  ]

  return (
    <div className="form-container mainContainer">
      <div className="row p-0 m-0 pb-5">
        <div className="col-12 col-md-2"></div>
        <div className="col-12 col-md-8">
          <div className="header-row">
            <h2 className="page-title">Class Master</h2>
          </div>

          <div className="search-bar">
            <Input
              placeholder="Search class..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 250 }}
            />

            <Button
              className="addButton mt-2 mt-md-0"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
              style={{ marginLeft: 10 }}
            >
              Add Class
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={filteredData}
            pagination={{ pageSize: 10 }}
            bordered
            loading={loading}
            scroll={{ x: 'max-content' }}
            className="shadow border rounded"
          />
        </div>
        <div className="col-12 col-md-2"></div>
      </div>

      <Modal
        title={editingId ? 'Edit Class' : 'Add Class'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>,
          <Button
            key="save"
            className="addButton"
            type="primary"
            onClick={handleSave}
          >
            Save
          </Button>,
        ]}
      >
        <div className="mb-3">
          <label>Class Name</label>
          <Input
            value={className}
            onChange={(e) => setClassName(e.target.value)}
            placeholder="Enter class name"
          />
        </div>

        {editingId && (
          <div>
            <label>Room Status</label>
            <br />
            <Switch
              checked={modalStatus}
              loading={updatingStatus}
              onChange={handleModalStatusChange}
              checkedChildren="ON"
              unCheckedChildren="OFF"
            />
          </div>
        )}
      </Modal>
    </div>
  )
}

export default ClassMaster
